/**
 * Bundle Pricing Logic
 * 
 * For products priced at £5.75 (individual library squares):
 * - Buy 2 for £10 total
 * - Buy 3 for £12.95 total
 * 
 * For quantities > 3, apply best combination:
 * - 4 items: 2x "2 for £10" = £20
 * - 5 items: "3 for £12.95" + "2 for £10" = £22.95
 * - 6 items: 2x "3 for £12.95" = £25.90
 * - etc.
 */

// The unit price for bundle-eligible items
export const BUNDLE_ELIGIBLE_PRICE = 5.75;

// Bundle pricing tiers
export const BUNDLE_PRICES = {
  TWO_FOR: 10,      // 2 items for £10
  THREE_FOR: 12.95, // 3 items for £12.95
};

export interface BundleResult {
  originalTotal: number;
  discountedTotal: number;
  savings: number;
  bundlesApplied: Array<{
    type: '2 for £10' | '3 for £12.95';
    count: number;
  }>;
}

/**
 * Calculate the optimal bundle pricing for a given quantity of £5.75 items
 */
export function calculateBundlePrice(quantity: number): BundleResult {
  const originalTotal = quantity * BUNDLE_ELIGIBLE_PRICE;
  
  if (quantity < 2) {
    return {
      originalTotal,
      discountedTotal: originalTotal,
      savings: 0,
      bundlesApplied: [],
    };
  }

  // Find the optimal combination using dynamic programming approach
  // Goal: minimize total cost
  let bestTotal = originalTotal;
  let bestCombination = { threes: 0, twos: 0, singles: quantity };

  // Try all possible combinations of "3 for £12" bundles
  const maxThrees = Math.floor(quantity / 3);
  
  for (let threes = 0; threes <= maxThrees; threes++) {
    const remaining = quantity - (threes * 3);
    const twos = Math.floor(remaining / 2);
    const singles = remaining - (twos * 2);
    
    const total = 
      (threes * BUNDLE_PRICES.THREE_FOR) + 
      (twos * BUNDLE_PRICES.TWO_FOR) + 
      (singles * BUNDLE_ELIGIBLE_PRICE);
    
    if (total < bestTotal) {
      bestTotal = total;
      bestCombination = { threes, twos, singles };
    }
  }

  const bundlesApplied: BundleResult['bundlesApplied'] = [];
  
  if (bestCombination.threes > 0) {
    bundlesApplied.push({ type: '3 for £12.95', count: bestCombination.threes });
  }
  if (bestCombination.twos > 0) {
    bundlesApplied.push({ type: '2 for £10', count: bestCombination.twos });
  }

  return {
    originalTotal,
    discountedTotal: bestTotal,
    savings: originalTotal - bestTotal,
    bundlesApplied,
  };
}

/**
 * Check if an item is eligible for bundle pricing
 * Items must be individual library squares priced at £5.75
 */
export function isBundleEligible(price: number, name?: string): boolean {
  // Must match the bundle-eligible price exactly (or very close due to floating point)
  if (Math.abs(price - BUNDLE_ELIGIBLE_PRICE) > 0.01) {
    return false;
  }
  
  // Additional check: exclude sets (they have their own pricing)
  if (name && name.toLowerCase().includes('set of')) {
    return false;
  }
  
  return true;
}

/**
 * Calculate bundle discount for a cart
 * Returns the total bundle discount to apply
 */
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface CartBundleResult {
  totalDiscount: number;
  bundleDetails: BundleResult | null;
  eligibleItemsCount: number;
}

export function calculateCartBundleDiscount(items: CartItem[]): CartBundleResult {
  // Count total bundle-eligible items in cart
  let eligibleItemsCount = 0;
  
  for (const item of items) {
    if (isBundleEligible(item.price, item.name)) {
      eligibleItemsCount += item.quantity;
    }
  }
  
  if (eligibleItemsCount < 2) {
    return {
      totalDiscount: 0,
      bundleDetails: null,
      eligibleItemsCount,
    };
  }
  
  const bundleResult = calculateBundlePrice(eligibleItemsCount);
  
  return {
    totalDiscount: bundleResult.savings,
    bundleDetails: bundleResult,
    eligibleItemsCount,
  };
}
